import {
  KEYBOARD_VIEWBOX_HEIGHT,
  keyFootprint,
} from "@/components/ui/molecules/mini-piano";

/**
 * Marks drawn **on** a `MiniPiano`, shared by the grip step and the practice
 * run so the same fact is drawn the same way on both.
 *
 * They are an overlay rather than a `MiniPiano` feature because each one is a
 * two-colour decision: a number has to read against a tinted white key and a
 * tinted black one, and a line joining keys crosses both. `MiniPiano` draws
 * keys; what is written on top of them belongs to the screen writing it.
 */

/** Height of the palm arc, in viewBox units, measured from the top. */
export const ARC_Y = 5;

/**
 * One lane per hand, for a screen that draws both at once.
 *
 * The identity of a hand moves from its **hue** to its **position**, and the
 * lanes are constant for the whole run so the rule is learnt in one group.
 * Two reasons it cannot stay a matter of colour: the two hand tokens sit at
 * 1.83:1 against each other (1.24:1 once tinted), where WCAG asks 3:1 between
 * adjacent meaningful graphics; and at a single shared height two crossed
 * hands drew two lines on top of each other, i.e. one line.
 *
 * Right above left, the way a staff puts treble above bass. Crossed hands
 * then read as the two lanes swapping ends, which costs nothing to draw.
 */
export const HAND_LANE_Y: Readonly<Record<"L" | "R", number>> = {
  R: 3,
  L: 9,
};

/** Baseline of the finger pills, near the bottom of the white keys. */
export const FINGER_Y = KEYBOARD_VIEWBOX_HEIGHT - 12;

const REGION_TINT_PCT = 45;
const WHITE_KEY_REST = "#ffffff";
const BLACK_KEY_REST = "#141416";
const BLACK_PITCH_CLASSES = new Set([1, 3, 6, 8, 10]);

export function isBlackKey(midi: number): boolean {
  return BLACK_PITCH_CLASSES.has(((midi % 12) + 12) % 12);
}

/**
 * The colour of a key that belongs to what is being asked for but is not
 * down: the hand colour mixed into the key's own rest colour, so a white key
 * and a black one both read as "this one, not yet".
 */
export function regionTint(midi: number, handColor: string): string {
  const rest = isBlackKey(midi) ? BLACK_KEY_REST : WHITE_KEY_REST;

  return `color-mix(in srgb, ${handColor} ${REGION_TINT_PCT}%, ${rest})`;
}

/**
 * The line joining the keys of one hand: "these go together".
 *
 * Drawn twice — a dark casing under a coloured stroke — the same idiom as
 * `PitchRail`, because it crosses white keys and black ones and a single
 * stroke legible on one is invisible on the other.
 */
export function HandArc({
  x1,
  x2,
  color,
  opacity = 0.9,
  y = ARC_Y,
  style,
}: {
  readonly x1: number;
  readonly x2: number;
  readonly color: string;
  readonly opacity?: number;
  readonly y?: number;
  readonly style?: React.CSSProperties;
}) {
  return (
    <>
      <line
        x1={x1}
        y1={y}
        x2={x2}
        y2={y}
        stroke="rgba(0,0,0,0.55)"
        strokeWidth={3.5}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1={x1}
        y1={y}
        x2={x2}
        y2={y}
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
        opacity={opacity}
        style={style}
      />
    </>
  );
}

/** A fingering digit on a key, in a disc so it reads on any key colour. */
export function FingerPill({
  x,
  mark,
  y = FINGER_Y,
}: {
  readonly x: number;
  readonly mark: string;
  readonly y?: number;
}) {
  return (
    <>
      <circle cx={x} cy={y} r={mark.length > 1 ? 4.4 : 3.4} fill="rgba(0,0,0,0.62)" />
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={mark.length > 1 ? 4 : 5}
        fontWeight={700}
        style={{ fill: "#ffffff", letterSpacing: "-0.02em" }}
      >
        {mark}
      </text>
    </>
  );
}

/**
 * A bar at the foot of a key: "your finger is already on this one".
 *
 * A third fact needs a third **channel**, not a third point on a scale of
 * intensity. A key to strike is painted on its *face*; a key already
 * sounding is marked at its *foot*, with the face left alone. A paler fill
 * would have read "press this one gently", which is the one thing it must
 * never say — and it is what 39.9% of the groups of a both-hands run need
 * to get right.
 */
/**
 * A caret over the key a pass starts on.
 *
 * Achromatic on purpose: every colour on this keyboard already means a hand,
 * and "this is where it begins" is not a fact about a hand. It sits in the
 * arc lane at the top of the key, where nothing else is drawn before a pass.
 */
export function StartMark({
  x,
  color = "var(--color-text-primary)",
}: {
  readonly x: number;
  readonly color?: string;
}) {
  const half = 2.6;
  const height = 3.2;
  const top = ARC_Y - 1.6;

  return (
    <polygon
      points={`${x - half},${top} ${x + half},${top} ${x},${top + height}`}
      fill={color}
      stroke="rgba(0,0,0,0.5)"
      strokeWidth={0.4}
      strokeLinejoin="round"
    />
  );
}

export function KeyFoot({
  x,
  midi,
  color,
}: {
  readonly x: number;
  readonly midi: number;
  readonly color: string;
}) {
  const { width, bottom } = keyFootprint(midi);
  const barWidth = width * 0.62;
  const barHeight = 3.4;

  return (
    <rect
      x={x - barWidth / 2}
      y={bottom - barHeight - 1.6}
      width={barWidth}
      height={barHeight}
      rx={1.2}
      fill={color}
      stroke="rgba(0,0,0,0.45)"
      strokeWidth={0.4}
    />
  );
}

/**
 * A chevron at the edge of the drawing: "a key of this hand is out there".
 *
 * The window is capped at four octaves, so a hand reaching past it loses keys
 * from the drawing. Renouncing silently — which is what happened — asks the
 * player to play a key that is nowhere on screen, the exact failure mode this
 * journey is written against. It becomes the normal way a wide two-hand unit
 * ends, so it has to be said.
 */
export function OutOfWindowChevron({
  side,
  viewWidth,
  y,
  color,
}: {
  readonly side: "low" | "high";
  readonly viewWidth: number;
  readonly y: number;
  readonly color: string;
}) {
  const x = side === "low" ? 2.5 : viewWidth - 2.5;
  const dx = side === "low" ? 3.2 : -3.2;

  return (
    <polyline
      points={`${x + dx},${y - 3} ${x},${y} ${x + dx},${y + 3}`}
      fill="none"
      stroke={color}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}
